import React, { useContext, useRef, useState } from "react";
import { hasDimensions, hasProductLine, assertHasDimensions } from "../../services/CabinetValidator";
import { DragInfo, getDragService, IDragService } from "../../services/CabinetService";
import { IVec2d } from "../../services/WebApiClient";
import { FrontPosConverter } from "../../util/PosConverter";
import { EditorContext } from "../EditorCommon/EditorContext";
import { InteriorDragContext } from "./InteriorImageDragContext";
import { subtract } from "../../models/IObj";

export interface ProperSvgElement extends SVGElement {
    getScreenCTM(): SVGMatrix;
    createSVGPoint(): SVGPoint;
}

type MEvent = React.MouseEvent<SVGGElement>;

export function InteriorImageDragHandler(props: {
    svgCanvas: ProperSvgElement | null,
    children?: React.ReactNode
}) {

    const ec = useContext(EditorContext);
    const cabinet = assertHasDimensions(ec.cabinet);
    const dragContext = useContext(InteriorDragContext);
    const ref = useRef<SVGRectElement | null>(null);
    let [dragService, setDragService] = useState(undefined as IDragService | undefined);
    let [dragInfo, setDragInfo] = useState(undefined as DragInfo | undefined);

    if (!hasDimensions(ec.cabinet))
        throw new Error("dimensions not defined");
    if (!hasProductLine(ec.cabinet))
        throw new Error("product line not defined");

    const convert = new FrontPosConverter(ec.cabinet.height!);

    const cabinetSize = {
        x: ec.cabinet.width,
        y: ec.cabinet.height,
    };

    

    function cancelDrag() {
        setDragService(undefined);
        setDragInfo(undefined);
        dragContext.setInfo(undefined);
    }

    function mouseOver(evt: MEvent) {
        if (!dragContext?.info)
            return;
        

        evt.preventDefault();

        if (!dragContext.info.isDragging)
            return;

        if (dragService)
            return;

        const modelPos = getModelPos(evt, props.svgCanvas, ec.cabinet.height!);
        dragService = getDragService(
            cabinet,
            ec.cabinetService,
            dragContext.info.subGroup,
            undefined,
            modelPos,
            dragContext.info.mouseOffset);
        setDragService(dragService);
        dragInfo = dragService.getDragInfo(modelPos, evt.ctrlKey);
        setDragInfo(dragInfo);
    }


    function mouseMove(evt: MEvent) {
        if (!dragContext.info)
            return;
        if (dragContext.info.isDragging && !isLeftMouseButtonPressed(evt)) {
            cancelDrag();
            return;
        }

        if (!dragContext.info.isDragging && isLeftMouseButtonPressed(evt)) {
            const minDist = 4;
            if (Math.abs(dragContext.info.initialCoords.x - evt.clientX) > minDist
                || Math.abs(dragContext.info.initialCoords.y - evt.clientY) > minDist) {

                dragContext.info = {
                    ...dragContext.info,
                    isDragging: true,
                };
                dragContext.setInfo(dragContext.info)
            }
        }

        if (dragContext.info.isDragging && !dragService) {

            const modelPos = getModelPos(evt, props.svgCanvas, ec.cabinet.height!);
            const initialMouseModelPos = getModelPos2(dragContext.info.initialCoords, props.svgCanvas, ec.cabinet.height!);
            const item = dragContext.info.item;
            if (!item)
                return;
            const mouseOffset = subtract(initialMouseModelPos, item.position);
            dragService = getDragService(
                cabinet,
                ec.cabinetService,
                dragContext.info.subGroup,
                dragContext.info.item,
                modelPos,
                mouseOffset
            );
            setDragService(dragService);
            dragInfo = dragService.getDragInfo(modelPos, evt.ctrlKey);
            setDragInfo(dragInfo);
        }
        else if (dragService) {
            const modelPos = getModelPos(evt, props.svgCanvas, ec.cabinet.height!);
            dragInfo = dragService.getDragInfo(modelPos, evt.ctrlKey);
            setDragInfo(dragInfo);
        }
    }

    function mouseDown(evt: MEvent) {
        dragContext.setInfo(undefined);
    }



    function mouseUp(evt: MEvent) {
        //TODO Move some of this into InteriorService
        if (!dragService)
            return;
        if (!dragContext.info)
            return;
        const dragInfo = dragService.getDragInfo(getModelPos(evt, props.svgCanvas, ec.cabinet.height!), evt.ctrlKey);
        let item = dragContext.info.item;
        if (dragInfo) {
            const items = dragService.place(dragInfo, evt.ctrlKey);
            ec.cabinetService.interior.setItems(items);
        }
        setDragService(undefined);
        setDragInfo(undefined);
        dragContext.setInfo({
            ...dragContext.info,
            isDragging: false,
            item: item,
        });
    }



    function mouseOut(evt: MEvent) {
        //setDragService(undefined);
    }

    const shadows = dragInfo?.shadows.map((shadow, index) => (
        <rect
            key={index}
            className="drag-shadow"
            {...convert.obj(shadow)}
        />
        ));

    let draggedItem;
    if (dragInfo) {
        const svgObj = convert.obj(dragInfo);
        const transformString = dragInfo.isFlipped
            ? convert.getFlipTransformString(dragInfo)
            : '';
        draggedItem = <image
            xlinkHref={dragInfo.textureUrl}
            preserveAspectRatio="none"
            transform={transformString}
            {...svgObj}
        />;

    }
    let selectRect;
    if (!dragInfo && dragContext.info?.item) {
        const item = ec.cabinet.interiorItems.find(i => i.index === dragContext.info?.item?.index);
        if (item) {
            const svgObj = convert.obj(item);
            selectRect = <rect {...svgObj} className="select-rect"  />
        }
    }

    return (
        <g>
            <g onMouseEnter={mouseOver}
                onMouseMove={mouseMove}
                onMouseDown={mouseDown}
                onMouseUp={mouseUp}
                onMouseLeave={mouseOut}
                ref={ref}
            >
                <rect className="interior-drag-spacer"
                    style={{ fill: "transparent" }}
                    {...convert.size(cabinetSize)}
                    {...convert.pos({ x: 0, y: 0 }, cabinetSize)}
                >

                </rect>
                {props.children}
                {shadows}
                {draggedItem}
                {selectRect}
            </g>
        </g>

    );
}



function isLeftMouseButtonPressed(evt: React.MouseEvent): boolean {
    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
    return (!!(evt.buttons & 1));
}



function getModelPos(evt: MEvent, svgCanvas: ProperSvgElement | null, cabinetHeight: number): IVec2d {
    let clientPos = {
        x: evt.clientX,
        y: evt.clientY,
    }; //TODO get position fron TouchEvent
    return getModelPos2(clientPos, svgCanvas, cabinetHeight);
}


function getModelPos2(clientPos: IVec2d, svgCanvas: ProperSvgElement | null, cabinetHeight: number): IVec2d {
    if (!svgCanvas)
        return clientPos;
    const svgPoint = svgCanvas.createSVGPoint();
    svgPoint.x = clientPos.x;
    svgPoint.y = clientPos.y;
    const svgCoordinate = svgPoint.matrixTransform(svgCanvas.getScreenCTM().inverse());
    const result = {
        x: svgCoordinate.x,
        y: cabinetHeight - svgCoordinate.y
    };
    return result;
}
